import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, FileText } from "lucide-react";
import dayjs from "dayjs";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminReviewPanel = () => {
  const [queue, setQueue] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/documents/review-queue").then((r) => setQueue(r.data)).catch(() => setQueue([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const review = async (docId, action, rejectReason) => {
    setBusy(true);
    try {
      await api.post(`/documents/${docId}/review`, { action, reason: rejectReason || null });
      toast.success(action === "verify" ? "Document verified" : "Document rejected");
      setRejecting(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (queue === null)
    return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#009900]" /></div>;

  return (
    <div data-testid="admin-review-panel">
      <p className="text-sm text-[#57685e] mb-5">{queue.length} document{queue.length !== 1 ? "s" : ""} awaiting review.</p>
      {queue.length === 0 ? (
        <div className="bg-white border border-[#e2ebe4] rounded-lg p-10 text-center text-sm text-[#57685e]" data-testid="review-queue-empty">
          Review queue is empty.
        </div>
      ) : (
        <div className="space-y-4">
          {queue.map((d) => (
            <div key={d.doc_id} className="bg-white border border-[#e2ebe4] rounded-lg p-4 flex flex-wrap items-center gap-4" data-testid={`review-row-${d.doc_id}`}>
              {d.content_type?.startsWith("image/") ? (
                <img src={`${API_BASE}/documents/${d.doc_id}/file`} alt={d.doc_label} className="w-28 h-20 object-cover rounded border border-[#e2ebe4]" />
              ) : (
                <div className="w-28 h-20 rounded border border-[#e2ebe4] bg-[#f4f7f4] flex items-center justify-center"><FileText className="w-7 h-7 text-[#57685e]" /></div>
              )}
              <div className="flex-1 min-w-[180px]">
                <div className="font-bold text-sm text-[#0F1E17]">{d.doc_label}</div>
                <div className="text-xs text-[#57685e]">{d.user_name} · {d.user_email}</div>
                <div className="text-[10px] text-[#8a978f]">{d.original_filename} · {dayjs(d.uploaded_at).format("DD MMM YYYY, HH:mm")}</div>
              </div>
              {rejecting === d.doc_id ? (
                <div className="flex gap-2 items-center w-full sm:w-auto">
                  <input className="field-input" placeholder="Rejection reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason-input" />
                  <button onClick={() => review(d.doc_id, "reject", reason)} disabled={busy}
                    className="bg-red-600 text-white rounded px-4 py-2.5 text-xs font-medium uppercase" data-testid="reject-confirm-btn">Reject</button>
                  <button onClick={() => setRejecting(null)} className="text-xs text-[#57685e] underline">Cancel</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => review(d.doc_id, "verify")} disabled={busy}
                    className="border-2 border-[#009900] text-[#009900] rounded px-4 py-2.5 text-xs font-medium uppercase tracking-wide hover:bg-[#f0f9f0] flex items-center gap-1.5"
                    data-testid="verify-doc-btn"><CheckCircle2 className="w-4 h-4" /> Verify</button>
                  <button onClick={() => { setRejecting(d.doc_id); setReason(""); }} disabled={busy}
                    className="border-2 border-red-500 text-red-600 rounded px-4 py-2.5 text-xs font-medium uppercase tracking-wide hover:bg-red-50 flex items-center gap-1.5"
                    data-testid="reject-doc-btn"><XCircle className="w-4 h-4" /> Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
